import React, { useState, useEffect, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import useDarkMode from './hooks/useDarkMode';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { apiClient } from './utils/apiClient';
import TenantLoginPage from './components/TenantLoginPage';
import TenantDashboard from './components/TenantDashboard';
import PersonnelManagement from './components/PersonnelManagement';
import DepartmentManagement from './components/DepartmentManagement';
import PositionManagement from './components/PositionManagement';
import LocationManagement from './components/LocationManagement';
import WorkingRegimeManagement from './components/WorkingRegimeManagement';
import ReportsPage from './components/ReportsPage';
import PersonnelReportsPage from './components/PersonnelReportsPage';
import SettingsPanel from './components/SettingsPanel';
import MailSettingsPage from './components/mail/MailSettingsPage';
import MailSubscriptionsPage from './components/mail/MailSubscriptionsPage';
import MailDispatchLogsPage from './components/mail/MailDispatchLogsPage';
import Sidebar from './components/Sidebar';
import DevicesPage from './pages/DevicesPage';
import DeviceReachabilityPage from './pages/DeviceReachabilityPage';
import LeaveManagementPage from './components/LeaveManagementPage';
import LeaveTypeManagement from './components/LeaveTypeManagement';
import RoleManagementPage from './components/RoleManagementPage';
import ManualEntryPage from './components/ManualEntryPage';
import AllPassRecordsPage from './components/AllPassRecordsPage';
import EmployeeAssignmentPage from './pages/EmployeeAssignmentPage';
import SchedulingDefinitionsPage from './pages/scheduling/SchedulingDefinitionsPage';
import PuantajControlPage from './pages/PuantajControlPage';
import PublicHolidaysPage from './pages/PublicHolidaysPage';
import DashboardSpecialDaysPage from './pages/DashboardSpecialDaysPage';
import AccessGroupsPage from "./pages/AccessGroupsPage";
import AccessSyncPage from "./pages/AccessSyncPage";

import EmployeeImportPage from './pages/EmployeeImportPage';
import LeaveBalanceImportPage from './pages/LeaveBalanceImportPage';


// 🔹 YENİ: İzin Bakiyesi sayfasını import ediyoruz
import LeaveBalanceManagement from './components/LeaveBalanceManagement';
import UserProfilePage from './pages/UserProfilePage';
import SecurityAuditLogsPage from './pages/SecurityAuditLogsPage';
import TenantSecuritySettingsPage from './pages/TenantSecuritySettingsPage';
import PayrollCalcSettingsPage from './pages/PayrollCalcSettingsPage';
import AnnouncementsPage from './pages/announcements/AnnouncementsPage';
import BulkOperationsPage from './pages/BulkOperationsPage';
import CustomReportsPage from './components/CustomReportsPage';
import RunCustomReportPage from './components/RunCustomReportPage';
import SupportTicketsPage from './pages/SupportTicketsPage';

import {
    Loader2, Menu, LogOut,
    LayoutGrid, Users, Building, HardHat, QrCode, ClipboardList,
    CalendarClock, CalendarDays, UserCheck, Calculator, FileText, Database, Settings,
    Edit3, CalendarPlus, UserCircle, List, ShieldCheck, Activity, FileBarChart, Radio
} from 'lucide-react';

// ===========================
// Authenticated content
// ===========================
function AuthenticatedAppContent() {
    const { 
        firmCode, 
        userRole, 
        handleLogout, 
        mustChangePassword, 
        trackActivity,
        isParentUser,
        allowedSubCompanies,
        activeSubCompanyCode,
        activeSubCompanyName,
        switchSubCompany
    } = useAuth();

    const [subCompaniesList, setSubCompaniesList] = useState([]);
    const [subCompaniesLoading, setSubCompaniesLoading] = useState(false);

    useEffect(() => {
        if (isParentUser) {
            const fetchSubs = async () => {
                setSubCompaniesLoading(true);
                try {
                    const data = await apiClient('Holding/sub-companies');
                    setSubCompaniesList(data || []);
                } catch (e) {
                    console.error("Alt firmalar çekilemedi:", e);
                } finally {
                    setSubCompaniesLoading(false);
                }
            };
            fetchSubs();
        }
    }, [isParentUser]);

    // 1) currentPage'i kalıcı hale getiriyoruz
    const [currentPage, setCurrentPage] = useState(() => {
        try {
            return localStorage.getItem('currentPage') || 'dashboard';
        } catch { return 'dashboard'; }
    });

    const pageNodeRefs = useRef({});
    const getPageNodeRef = (key) => {
        if (!pageNodeRefs.current[key]) {
            pageNodeRefs.current[key] = React.createRef();
        }
        return pageNodeRefs.current[key];
    };

    // Sayfa değiştiğinde aktiviteyi tazele (Session sliding için)
    useEffect(() => {
        if (trackActivity) trackActivity();
    }, [currentPage, trackActivity]);

    // Forced redirect for password change
    useEffect(() => {
        if (mustChangePassword && currentPage !== 'user_profile') {
            setCurrentPage('user_profile');
        }
    }, [mustChangePassword, currentPage]);

    // 2) currentPage değiştiğinde kaydet
    useEffect(() => {
        try { localStorage.setItem('currentPage', currentPage); } catch { }
    }, [currentPage]);

    // Sidebar genişlik state'i
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        try {
            const saved = localStorage.getItem('isSidebarCollapsed');
            return saved ? JSON.parse(saved) : false;
        } catch {
            return false;
        }
    });

    // Navbar Konum State'i ('left' | 'right' | 'top' | 'bottom')
    const [navPosition, setNavPosition] = useState(() => {
        try {
            const saved = localStorage.getItem('tenant_nav_position');
            return ['left', 'right', 'top', 'bottom'].includes(saved) ? saved : 'left';
        } catch {
            return 'left';
        }
    });

    const handleChangeNavPosition = (newPos) => {
        if (!['left', 'right', 'top', 'bottom'].includes(newPos)) return;
        setNavPosition(newPos);
        try {
            localStorage.setItem('tenant_nav_position', newPos);
        } catch { }
    };

    const sidebarRef = useRef(null);
    const [darkMode, toggleDarkMode] = useDarkMode();

    // Sidebar collapsed state'ini kaydet
    useEffect(() => {
        try {
            localStorage.setItem('isSidebarCollapsed', JSON.stringify(isSidebarCollapsed));
        } catch { }
    }, [isSidebarCollapsed]);

    // Menü öğesi tıklaması
    const handleMenuItemClick = (pageName) => {
        setCurrentPage(pageName);
        if (window.innerWidth < 768) {
            setIsSidebarCollapsed(true);
        }
    };

    // Sayfa render switch
    const renderPage = () => {
        switch (currentPage) {
            case 'dashboard': return <TenantDashboard onNavigate={setCurrentPage} />;
            case 'personnel': return <PersonnelManagement />;
            case 'departments': return <DepartmentManagement />;
            case 'positions': return <PositionManagement />;
            case 'locations': return <LocationManagement />;
            case 'working_regimes': return <WorkingRegimeManagement />;
            case 'pass_records_list': return <AllPassRecordsPage />;
            case 'manual_pass_record': return <ManualEntryPage />;
            case 'leave_management': return <LeaveManagementPage />;
            case 'pdks_reports_page': return <ReportsPage />;
            case 'personnel_reports_page': return <PersonnelReportsPage />;
            case 'settings': return (
                <SettingsPanel
                    navPosition={navPosition}
                    onChangeNavPosition={handleChangeNavPosition}
                    isSidebarCollapsed={isSidebarCollapsed}
                    onToggleSidebar={() => setIsSidebarCollapsed(prev => !prev)}
                    onNavigate={setCurrentPage}
                />
            );
            case 'security_settings': return <TenantSecuritySettingsPage onNavigate={setCurrentPage} />;
            case 'device_reachability': return <DeviceReachabilityPage />;
            case 'payroll_calc_settings': return <PayrollCalcSettingsPage />;


            // --- Mail Yönetimi (Ayarlar alt menüsü) ---
            case 'mail_settings': return <MailSettingsPage />;
            case 'mail_subscriptions': return <MailSubscriptionsPage />;
            case 'mail_dispatch_logs': return <MailDispatchLogsPage />;
            case 'devices': return <DevicesPage />;
            case 'leave_types': return <LeaveTypeManagement />;
            case 'scheduling_definitions': return <SchedulingDefinitionsPage />;
            case 'employee_assignment': return <EmployeeAssignmentPage />;
            case 'puantaj_control': return <PuantajControlPage />;
            case 'public_holidays': return <PublicHolidaysPage />;
            case 'dashboard_special_days': return <DashboardSpecialDaysPage />;
            case 'role_management': return <RoleManagementPage />;
            case 'bulk_operations': return <BulkOperationsPage />;

            // --- yeni sayfalar ---
            case 'access_groups': return <AccessGroupsPage />;
            case 'access_sync': return <AccessSyncPage />;
            case 'employee_import': return <EmployeeImportPage />;
            case 'leave_balance_import': return <LeaveBalanceImportPage />;

            // 🔹 YENİ: İzin Bakiyeleri sayfası
            case 'leave_balances': return <LeaveBalanceManagement />;

            case 'user_profile': return <UserProfilePage />;
            case 'security_audit_logs': return <SecurityAuditLogsPage />;
            case 'announcements': return <AnnouncementsPage />;
            case 'support_tickets': return <SupportTicketsPage handleNavigation={setCurrentPage} />;
            case 'custom_reports': return <CustomReportsPage handleNavigation={setCurrentPage} />;
            default: 
                if (currentPage.startsWith('run_custom_report_')) {
                    const reportId = currentPage.split('_').pop();
                    return <RunCustomReportPage reportId={reportId} handleNavigation={setCurrentPage} />;
                }
                return <TenantDashboard onNavigate={setCurrentPage} />;
        }
    };

    // Sidebar dışına tıklayınca kapat (mobil)
    useEffect(() => {
        function handleClickOutside(event) {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target) &&
                window.innerWidth < 768 && !isSidebarCollapsed) {
                setIsSidebarCollapsed(true);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isSidebarCollapsed, sidebarRef]);

    // Resize davranışı
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) setIsSidebarCollapsed(true);
            else setIsSidebarCollapsed(false);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const pageTitles = {
        dashboard: { title: 'PDKS Yönetim Paneli', icon: <LayoutGrid className="w-6 h-6 mr-2 text-blue-500" /> },
        personnel: { title: 'Personel Yönetimi', icon: <Users className="w-6 h-6 mr-2 text-blue-500" /> },
        departments: { title: 'Bölüm Yönetimi', icon: <Building className="w-6 h-6 mr-2 text-blue-500" /> },
        positions: { title: 'Pozisyon Yönetimi', icon: <HardHat className="w-6 h-6 mr-2 text-blue-500" /> },
        locations: { title: 'Lokasyon Yönetimi', icon: <Building className="w-6 h-6 mr-2 text-blue-500" /> },
        working_regimes: { title: 'Çalışma Rejimi Yönetimi', icon: <Building className="w-6 h-6 mr-2 text-blue-500" /> },
        pass_records_list: { title: 'Tüm Geçiş Kayıtları', icon: <List className="w-6 h-6 mr-2 text-blue-500" /> },
        manual_pass_record: { title: 'Manuel Kayıt', icon: <Edit3 className="w-6 h-6 mr-2 text-blue-500" /> },
        leave_management: { title: 'İzin Yönetimi', icon: <CalendarPlus className="w-6 h-6 mr-2 text-blue-500" /> },
        pdks_reports_page: { title: 'PDKS Raporları', icon: <Database className="w-6 h-6 mr-2 text-blue-500" /> },
        personnel_reports_page: { title: 'Sicil Raporları', icon: <FileText className="w-6 h-6 mr-2 text-blue-500" /> },
        settings: { title: 'Genel Ayarlar', icon: <Settings className="w-6 h-6 mr-2 text-blue-500" /> },
        security_settings: { title: 'Güvenlik Ayarları', icon: <ShieldCheck className="w-6 h-6 mr-2 text-blue-500" /> },
        device_reachability: { title: 'Cihaz Erişimleri', icon: <Radio className="w-6 h-6 mr-2 text-emerald-500" /> },
        payroll_calc_settings: { title: 'Otomatik Hesaplama Ayarları', icon: <Calculator className="w-6 h-6 mr-2 text-blue-500" /> },
        mail_settings: { title: 'Mail Sistem Ayarları', icon: <Settings className="w-6 h-6 mr-2 text-blue-500" /> },
        mail_subscriptions: { title: 'Mail Abonelikleri', icon: <Settings className="w-6 h-6 mr-2 text-blue-500" /> },
        mail_dispatch_logs: { title: 'Mail Gönderim Logları', icon: <Settings className="w-6 h-6 mr-2 text-blue-500" /> },
        devices: { title: 'Cihaz Yönetimi', icon: <QrCode className="w-6 h-6 mr-2 text-blue-500" /> },

        // yeni:
        access_groups: { title: 'Yetki Grupları', icon: <ShieldCheck className="w-6 h-6 mr-2 text-blue-500" /> },
        access_sync: { title: 'Yetki Senkronu', icon: <Database className="w-6 h-6 mr-2 text-blue-500" /> },
        employee_import: { title: 'Personel Aktarım', icon: <ClipboardList className="w-6 h-6 mr-2 text-blue-500" /> },
        leave_balance_import: { title: 'İzin Bakiye Aktarımı', icon: <ClipboardList className="w-6 h-6 mr-2 text-blue-500" /> },

        leave_types: { title: 'İzin Türü Yönetimi', icon: <ClipboardList className="w-6 h-6 mr-2 text-blue-500" /> },
        leave_type_management: { title: 'İzin Türü Yönetimi', icon: <ClipboardList className="w-6 h-6 mr-2 text-blue-500" /> },
        security_audit_logs: { title: 'Güvenlik ve Denetim Kayıtları', icon: <ShieldCheck className="w-6 h-6 mr-2 text-blue-500" /> },
        scheduling_definitions: { title: 'Vardiya ve Puantaj Ayarları', icon: <CalendarClock className="w-6 h-6 mr-2 text-blue-500" /> },
        employee_assignment: { title: 'Personel-Vardiya Atama', icon: <UserCheck className="w-6 h-6 mr-2 text-blue-500" /> },
        puantaj_control: { title: 'Puantaj Kontrol', icon: <Calculator className="w-6 h-6 mr-2 text-blue-500" /> },
        public_holidays: { title: 'Resmi Tatil Yönetimi', icon: <CalendarDays className="w-6 h-6 mr-2 text-blue-500" /> },
        dashboard_special_days: { title: 'Özel Gün & Temalar', icon: <CalendarDays className="w-6 h-6 mr-2 text-blue-500" /> },
        role_management: { title: 'Rol Yönetimi', icon: <ShieldCheck className="w-6 h-6 mr-2 text-blue-500" /> },
        bulk_operations: { title: 'Toplu İşlemler', icon: <Users className="w-6 h-6 mr-2 text-blue-500" /> },

        // 🔹 YENİ: Başlık barı için İzin Bakiyeleri
        leave_balances: { title: 'İzin Bakiyeleri', icon: <ClipboardList className="w-6 h-6 mr-2 text-blue-500" /> },

        user_profile: { title: 'Kullanıcı Profil Sayfası', icon: <UserCircle className="w-6 h-6 mr-2 text-blue-500" /> },
        announcements: { title: 'Duyurular', icon: <FileText className="w-6 h-6 mr-2 text-blue-500" /> },
        support_tickets: { title: 'Destek ve Yardım', icon: <Activity className="w-6 h-6 mr-2 text-blue-500" /> },
        custom_reports: { title: 'Özel Raporlar (Wizards)', icon: <FileBarChart className="w-6 h-6 mr-2 text-blue-500" /> },
    };

    const currentTitleConfig = pageTitles[currentPage] || 
        (currentPage.startsWith('run_custom_report_') 
            ? { title: 'Özel Rapor Çalıştır', icon: <FileBarChart className="w-6 h-6 mr-2 text-blue-500" /> }
            : { title: 'Sayfa Başlığı', icon: null });

    const isHorizontalNav = navPosition === 'top' || navPosition === 'bottom';
    const mainMarginClass = isHorizontalNav
        ? (navPosition === 'top' ? 'pt-16 w-full' : 'pb-16 w-full')
        : navPosition === 'right'
            ? (isSidebarCollapsed ? 'md:mr-[72px]' : 'md:mr-[260px]')
            : (isSidebarCollapsed ? 'md:ml-[72px]' : 'md:ml-[260px]');

    const mainWidthStyle = isHorizontalNav
        ? { width: '100%' }
        : { width: isSidebarCollapsed ? 'calc(100% - 72px)' : 'calc(100% - 260px)' };

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300 relative flex">
            {/* Sidebar / Adaptive Navbar */}
            <Sidebar
                currentPage={currentPage}
                handleMenuItemClick={handleMenuItemClick}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={() => setIsSidebarCollapsed(prev => !prev)}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                sidebarRef={sidebarRef}
                userRole={userRole}
                handleLogout={handleLogout}
                navPosition={navPosition}
                onChangeNavPosition={handleChangeNavPosition}
            />

            {/* Main Content */}
            <main
                className={`flex-1 flex flex-col transition-all duration-300 ${mainMarginClass}`}
                style={mainWidthStyle}
            >
                {/* Header */}
                <header className="flex items-center justify-between p-4 bg-white dark:bg-[#18191c] border-b dark:border-white/5 shadow-md z-30">
                    <div className="flex items-center">
                        {!isHorizontalNav && (
                            <button
                                onClick={() => setIsSidebarCollapsed(prev => !prev)}
                                className="md:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 mr-4"
                                title={isSidebarCollapsed ? 'Menüyü Genişlet' : 'Menüyü Daralt'}
                            >
                                <Menu size={20} />
                            </button>
                        )}
                        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 flex items-center">
                            {currentTitleConfig.icon}
                            {currentTitleConfig.title}
                        </h1>
                    </div>
                    <div className="flex items-center space-x-4">
                        {isParentUser && (
                            <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Aktif Şube/Firma:</span>
                                <select
                                    value={activeSubCompanyCode || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (!val) {
                                            switchSubCompany(null, null);
                                        } else {
                                            const selected = subCompaniesList.find(s => s.firmCode === val);
                                            switchSubCompany(val, selected?.firmName || val);
                                        }
                                    }}
                                    disabled={subCompaniesLoading}
                                    className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block transition-all font-semibold"
                                >
                                    <option value="">{firmCode.toUpperCase()} (Ana Firma)</option>
                                    {subCompaniesList.map(sub => (
                                        <option key={sub.firmCode} value={sub.firmCode}>
                                            {sub.firmName} ({sub.firmCode.toUpperCase()})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <span className="text-gray-700 dark:text-gray-300 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-lg">
                            Giriş: <span className="font-bold">{firmCode.toUpperCase()}</span>
                        </span>
                    </div>
                </header>

                {/* İçerik */}
                <div className="flex-1 overflow-y-auto p-4 relative">
                    <TransitionGroup component={null}>
                        <CSSTransition
                            key={currentPage}
                            nodeRef={getPageNodeRef(currentPage)}
                            classNames="page"
                            timeout={500}
                            unmountOnExit
                        >
                            <div ref={getPageNodeRef(currentPage)} className="h-full">
                                {renderPage()}
                            </div>
                        </CSSTransition>
                    </TransitionGroup>
                </div>
            </main>
        </div>
    );
}

// ===========================
// Root App
// ===========================
function App() {
    return (
        <AuthProvider>
            <AppContentRouter />
        </AuthProvider>
    );
}

function AppContentRouter() {
    const { isAuthenticated, firmCode, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
                <Loader2 className="w-10 h-10 animate-spin mr-3" />
                Uygulama yükleniyor...
            </div>
        );
    }

    if (!isAuthenticated || !firmCode) {
        return <TenantLoginPage />;
    }

    return <AuthenticatedAppContent />;
}

export default App;
